// src/router/index.js
import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/authStore";

// App
import home from "@/pages/home/index";

import budgetIndex from "@/pages/budgets/index";
import budgetCreate from "@/pages/budgets/create";
import budgetEdit from "@/pages/budgets/edit";
import budgetShow from "@/pages/budgets/show";

import customerIndex from "@/pages/customers/index";
import customerCreate from "@/pages/customers/create";
import customerEdit from "@/pages/customers/edit";
import customerShow from "@/pages/customers/show";

import employeeIndex from "@/pages/employees/index";
import employeeCreate from "@/pages/employees/create";
import employeeEdit from "@/pages/employees/edit";
import employeeShow from "@/pages/employees/show";

import projectIndex from "@/pages/projects/index";
import projectCreate from "@/pages/projects/create";
import projectEdit from "@/pages/projects/edit";
import projectShow from "@/pages/projects/show";

import serviceIndex from "@/pages/services/index";
import serviceCreate from "@/pages/services/create";
import serviceEdit from "@/pages/services/edit";
import serviceShow from "@/pages/services/show";

// Public
import login from "@/pages/auth/login";
import logout from "@/pages/auth/logout";
import register from "@/pages/auth/register";
import forgotPassword from '@/pages/auth/forgotPassword.vue';
import resetPassword from '@/pages/auth/resetPassword.vue';
import notFound from "@/pages/notFound";
import notAuthorized from "@/pages/notAuthorized";

// Layout
import privateLayout from "@/layouts/privateLayout";
import publicLayout from "@/layouts/publicLayout";
import maintenance from "@/pages/maintenance.vue";

const isMaintenance = process.env.VUE_APP_MAINTENANCE_MODE === "true";

const routes = isMaintenance ? [
  {
    path: "/:catchAll(.*)",
    name: "maintenance",
    component: maintenance,
  },
] : [
  {
    path: "/",
    component: privateLayout,
    children: [
      {
        path: "/",
        name: "home",
        component: home,
      },

      { path: "/budgets", name: "budgets-index", component: budgetIndex },
      { path: "/budgets/create", name: "budgets-create", component: budgetCreate },
      { path: "/budgets/:id/edit", name: "budgets-edit", component: budgetEdit, props: true },
      { path: "/budgets/:id", name: "budgets-show", component: budgetShow, props: true },

      { path: "/customers", name: "customers-index", component: customerIndex },
      { path: "/customers/create", name: "customers-create", component: customerCreate },
      { path: "/customers/:id/edit", name: "customers-edit", component: customerEdit, props: true },
      { path: "/customers/:id", name: "customers-show", component: customerShow, props: true },

      { path: "/employees", name: "employees-index", component: employeeIndex },
      { path: "/employees/create", name: "employees-create", component: employeeCreate },
      { path: "/employees/:id/edit", name: "employees-edit", component: employeeEdit, props: true },
      { path: "/employees/:id", name: "employees-show", component: employeeShow, props: true },

      { path: "/projects", name: "projects-index", component: projectIndex },
      { path: "/projects/create", name: "projects-create", component: projectCreate },
      { path: "/projects/:id/edit", name: "projects-edit", component: projectEdit, props: true },
      { path: "/projects/:id", name: "projects-show", component: projectShow, props: true },

      { path: "/services", name: "services-index", component: serviceIndex },
      { path: "/services/create", name: "services-create", component: serviceCreate },
      { path: "/services/:id/edit", name: "services-edit", component: serviceEdit, props: true },
      { path: "/services/:id", name: "services-show", component: serviceShow, props: true },

      {
        path: "/401",
        name: "not-authorized",
        component: notAuthorized,
      },
    ],
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/",
    component: publicLayout,
    children: [
      {
        path: "/login",
        name: "login",
        component: login,
      },
      {
        path: "/logout",
        name: "logout",
        component: logout,
      },
      {
        path: "/register",
        name: "register",
        component: register,
      },
      {
        path: '/forgot-password',
        name: 'forgot-password',
        component: forgotPassword,
      },
      {
        path: '/reset-password',
        name: 'reset-password',
        component: resetPassword,
      },
      {
        path: "/:pathMatch(.*)*",
        name: "not-found",
        component: notFound
      },
    ],
    meta: {
      requireAuth: false,
    },
  }
];

const router = createRouter({ history: createWebHistory(), routes });

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const requiresAuth = to.matched.some((x) => x.meta.requireAuth);
  const roles = to.meta.roles;

  // Verificar se o usuário está autenticado
  if (requiresAuth && !authStore.isAuthenticated  && to.name !== 'login') {
    next({ name: "login" });
    return;
  }

  // Verificação de papel (roles)
  if (roles) {
    const userRoles = authStore.user?.roles || [];
    const authorized = roles.some((role) => userRoles.includes(role));

    if (!authorized) {
      next({ name: "not-authorized" });
      return;
    }
  }

  // Verificar a expiração do token
  const expiresAt = localStorage.getItem("expiresAt");
  if (expiresAt && new Date().getTime() > expiresAt) {
    authStore.logout();
    next({ name: "login" });
    return;
  }

  next();
});
export default router;
